import {ActionTree} from 'vuex';
import briefService from '@/brief-hub/brief/BriefService';
import {MutationsType} from './mutations';
import {BriefState} from './types';
import {briefOrPayloadIsNull, isPayloadDifferent, saveAndUpdateBrief, updateBrief} from '@/brief-hub/store/modules/brief/actions-utils';
import {
    BriefSubmissionRequest,
    FreelancerBriefRequest,
    FreelancerJobTitleBriefRequest,
    FreelancerSkillsBriefRequest,
    MissionDescriptionBriefRequest,
    WhenConditionsBriefRequest,
    WhereConditionsBriefRequest,
} from '@/common/api/api-raw-types';

export enum ActionsType {
    GET_BRIEF = 'getBrief',
    SAVE_BRIEF = 'saveBrief',
    SAVE_BRIEF_FREELANCER_PROFILE = 'saveBriefFreelancerProfile',
    SAVE_BRIEF_FREELANCER_JOB_TITLE = 'saveBriefFreelancerJobTitle',
    SAVE_BRIEF_FREELANCER_SKILLS = 'saveBriefFreelancerSkills',
    SAVE_BRIEF_WHEN_CONDITIONS = 'saveBriefWhenConditions',
    SAVE_BRIEF_WHERE_CONDITIONS = 'saveBriefWhereConditions',
    SAVE_BRIEF_MISSION_DESCRIPTION = 'saveBriefMissionDescription',
    SUBMIT_BRIEF = 'submitBrief',
}

export const actions: ActionTree<BriefState, {}> = {
    [ActionsType.GET_BRIEF]: ({commit, state}, briefId: string) => {
        state.loading = true;
        state.error = false;

        return briefService.retrieveBrief(briefId).then((response) => {
            state.loading = false;
            const [data, error] = response;
            if (!data || error) {
                state.error = true;
                return [null, error];
            }
            return updateBrief(data, commit);
        });
    },

    [ActionsType.SAVE_BRIEF]: ({dispatch, state}) => {
        switch (state.payload!.type) {
            case 'FreelancerBriefRequest':
                return dispatch(ActionsType.SAVE_BRIEF_FREELANCER_PROFILE);
            case 'FreelancerJobTitleBriefRequest':
                return dispatch(ActionsType.SAVE_BRIEF_FREELANCER_JOB_TITLE);
            case 'FreelancerSkillsBriefRequest':
                return dispatch(ActionsType.SAVE_BRIEF_FREELANCER_SKILLS);
            case 'WhenConditionsBriefRequest':
                return dispatch(ActionsType.SAVE_BRIEF_WHEN_CONDITIONS);
            case 'WhereConditionsBriefRequest':
                return dispatch(ActionsType.SAVE_BRIEF_WHERE_CONDITIONS);
            case 'MissionDescriptionBriefRequest':
                return dispatch(ActionsType.SAVE_BRIEF_MISSION_DESCRIPTION);
            default:
                return [null, new Error('Unknown payload type')];
        }
    },

    [ActionsType.SAVE_BRIEF_FREELANCER_PROFILE]: ({commit, state}) => {
        const {brief, payload} = state;
        if (!payload) {
            return [null, new Error('payload is null')];
        }
        // brief does not exist => so we create it
        if (!brief) {
            return saveAndUpdateBrief(() => briefService.createBrief(payload.payload as FreelancerBriefRequest), commit);
        }
        // brief exists => so we update it
        const requestPayload = payload.payload as FreelancerBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.profileDetails)) {
            return saveAndUpdateBrief(() => brief.saveFreelancerProfile(requestPayload), commit);
        }
        commit(MutationsType.UPDATE_BRIEF, brief);
        return [brief, null];
    },

    [ActionsType.SAVE_BRIEF_FREELANCER_JOB_TITLE]: ({commit, state}) => {
        const {brief, payload} = state;
        if (!brief || !payload) {
            return briefOrPayloadIsNull();
        }
        const requestPayload = payload.payload as FreelancerJobTitleBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.profileDetails)) {
            return saveAndUpdateBrief(() => brief.saveFreelancerJobTitle(requestPayload), commit);
        }
        return updateBrief(brief, commit);
    },

    [ActionsType.SAVE_BRIEF_FREELANCER_SKILLS]: ({commit, state}) => {
        const {brief, payload} = state;
        if (!brief || !payload) {
            return briefOrPayloadIsNull();
        }
        const requestPayload = payload.payload as FreelancerSkillsBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.profileDetails)) {
            return saveAndUpdateBrief(() => brief.saveFreelancerSkills(requestPayload), commit);
        }
        return updateBrief(brief, commit);
    },

    [ActionsType.SAVE_BRIEF_WHEN_CONDITIONS]: ({commit, state}) => {
        const {brief, payload} = state;
        if (!brief || !payload) {
            return briefOrPayloadIsNull();
        }
        const requestPayload = payload.payload as WhenConditionsBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.briefDetails)) {
            return saveAndUpdateBrief(() => brief.saveWhenConditions(requestPayload), commit);
        }
        return updateBrief(brief, commit);
    },

    [ActionsType.SAVE_BRIEF_WHERE_CONDITIONS]: ({commit, state}) => {
        const {brief, payload} = state;
        if (!brief || !payload) {
            return briefOrPayloadIsNull();
        }
        const requestPayload = payload.payload as WhereConditionsBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.briefDetails)) {
            return saveAndUpdateBrief(() => brief.saveWhereConditions(requestPayload), commit);
        }
        return updateBrief(brief, commit);
    },

    [ActionsType.SAVE_BRIEF_MISSION_DESCRIPTION]: ({commit, state}) => {
        const {brief, payload} = state;
        if (!brief || !payload) {
            return briefOrPayloadIsNull();
        }
        const requestPayload = payload.payload as MissionDescriptionBriefRequest;
        if (isPayloadDifferent(requestPayload, brief.briefDetails)) {
            return saveAndUpdateBrief(() => brief.saveMissionDescription(requestPayload), commit);
        }
        return updateBrief(brief, commit);
    },

    [ActionsType.SUBMIT_BRIEF]: ({commit, state}) => {
        const {brief, payload} = state;
        if (!brief || !payload) {
            return briefOrPayloadIsNull();
        }
        const requestPayload = payload.payload as BriefSubmissionRequest;
        return saveAndUpdateBrief(() => brief.submitBrief(requestPayload), commit);
    },
};
